/**
 * Recording TanStack Query Hooks (T125/T126)
 * Data fetching and mutations for recording management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { type ApiResponse } from '../lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface Recording {
  id: string;
  appointmentId: string;
  zoomRecordingId?: string | null;
  videoUrl?: string | null;
  audioUrl?: string | null;
  transcriptUrl?: string | null;
  transcript?: string | null;
  thumbnailUrl?: string | null;
  duration: number | null;
  fileSize: number | null;
  processingStatus: 'PROCESSING' | 'AVAILABLE' | 'ARCHIVED' | 'UNAVAILABLE' | 'ERROR';
  recordedAt: string | null;
  uploadedAt?: string | null;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  // Computed fields from API
  customerName?: string;
  customerEmail?: string;
  appointmentTitle?: string;
  appointmentStartTime?: string;
  fileSizeFormatted?: string | null;
  durationFormatted?: string | null;
  hasVideo?: boolean;
  hasAudio?: boolean;
  hasTranscript?: boolean;
  accessGrantCount?: number;
  // Nested from detail endpoint
  customer?: {
    id: string;
    name: string;
    email: string;
    phone?: string | null;
  };
  appointment?: {
    id: string;
    title: string;
    description?: string;
    startTime: string;
    endTime?: string;
    duration?: number;
    status?: string;
  };
  accessGrants?: AccessGrant[];
}

export interface AccessGrant {
  id: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  grantedAt: string;
  expiresAt: string | null;
  accessCount: number;
  lastAccessedAt: string | null;
  isExpired: boolean;
}

export interface RecordingsResponse {
  recordings: Recording[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface TranscriptSearchResult {
  recordingId: string;
  appointmentId: string;
  customerName: string;
  appointmentDate: string;
  appointmentTitle: string;
  snippet: string;
  duration: number | null;
}

export interface TranscriptSearchResponse {
  results: TranscriptSearchResult[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface RecordingFilters {
  page?: number;
  limit?: number;
  customerId?: string;
  processingStatus?: string;
  hasAccess?: boolean;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useRecordings(filters: RecordingFilters = {}) {
  return useQuery({
    queryKey: ['recordings', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }
      if (filters.customerId) {
        params.append('customerId', filters.customerId);
      }
      if (filters.processingStatus) {
        params.append('processingStatus', filters.processingStatus);
      }
      if (filters.hasAccess !== undefined) {
        params.append('hasAccess', filters.hasAccess.toString());
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
      if (filters.sortBy) {
        params.append('sortBy', filters.sortBy);
      }
      if (filters.sortOrder) {
        params.append('sortOrder', filters.sortOrder);
      }

      const response = await apiClient.get<ApiResponse<RecordingsResponse>>(
        `/recordings?${params.toString()}`
      );
      return response.data.data;
    },
    staleTime: 30_000,
  });
}

export function useRecording(id: string | null) {
  return useQuery({
    queryKey: ['recording', id],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Recording>>(`/recordings/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useTranscriptSearch(query: string, page = 1) {
  return useQuery({
    queryKey: ['transcript-search', query, page],
    queryFn: async () => {
      const params = new URLSearchParams({ q: query, page: page.toString() });
      const response = await apiClient.get<ApiResponse<TranscriptSearchResponse>>(
        `/recordings/search?${params.toString()}`
      );
      return response.data.data;
    },
    enabled: query.length >= 2,
    staleTime: 60_000,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useGrantAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordingId,
      customerId,
      notify = true,
      expiresAt,
    }: {
      recordingId: string;
      customerId: string;
      notify?: boolean;
      expiresAt?: string;
    }) => {
      const response = await apiClient.post<ApiResponse<AccessGrant>>(
        `/recordings/${recordingId}/access`,
        { customerId, notify, expiresAt }
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recording', variables.recordingId] });
      queryClient.invalidateQueries({ queryKey: ['recordings'] });
    },
  });
}

export function useRevokeAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ recordingId, accessId }: { recordingId: string; accessId: string }) => {
      await apiClient.delete(`/recordings/${recordingId}/access/${accessId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordings'] });
      queryClient.invalidateQueries({ queryKey: ['recording'] });
    },
  });
}

export function useStreamUrl(recordingId: string | null) {
  return useQuery({
    queryKey: ['recording-stream', recordingId],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<{ streamUrl: string; expiresIn: number }>>(
        `/recordings/${recordingId}/stream`
      );
      return response.data.data;
    },
    enabled: !!recordingId,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

export function useDownloadUrl() {
  return useMutation({
    mutationFn: async ({
      recordingId,
      type = 'video',
    }: {
      recordingId: string;
      type?: 'video' | 'audio' | 'transcript';
    }) => {
      const response = await apiClient.get<
        ApiResponse<{ downloadUrl: string; expiresIn: number; type: string }>
      >(`/recordings/${recordingId}/download?type=${type}`);
      return response.data.data;
    },
  });
}

// ============================================================================
// Helpers
// ============================================================================

export function formatDuration(seconds: number | null): string {
  if (!seconds) {
    return '—';
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}h ${m}m`;
  }
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
}

export function formatFileSize(bytes: number | null): string {
  if (!bytes) {
    return '—';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(1)} ${units[i]}`;
}
